import { useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Coins, IndianRupee, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SummaryCard } from '@/components/SummaryCard';
import { DataTable, nextSortDirection, type SortDirection } from '@/components/DataTable';
import { useBalancesQuery } from '@/api/query/balance';
import { useAppContext } from '@/contexts/AppContext';
import { useDrawers } from '@/contexts/DrawerContext';
import type { BalanceListItem } from '@/types';
import { formatCurrency } from '@/lib/payments';
import { BALANCE_RATE } from '@/lib/balances';
import { useTableFirstScroll } from '@/hooks/useTableFirstScroll';
import { useTableQuery } from '@/hooks/useTableQuery';
import { createColumns } from './TableColumns';

type BalanceFilter = 'all' | 'due' | 'cleared';

const FILTERS: { id: BalanceFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'due', label: 'Balance Due' },
  { id: 'cleared', label: 'Cleared' },
];

const matchesFilter = (item: BalanceListItem, filter: BalanceFilter) => {
  if (filter === 'due') return item.balanceDue > 0;
  if (filter === 'cleared') return item.balanceDue <= 0;
  return true;
};

/** Balances page: every member who had a Paid by PMS amount, what they owe and what they repaid. */
export default function BalanceManagement() {
  const { pageRef, tableLocked } = useTableFirstScroll();
  const { isAuthenticated } = useAppContext();
  const { openDrawer } = useDrawers();
  const { searchTerm, setSearchTerm, filter, setFilter, page, setPage, pageSize, setPageSize, pageQuery } =
    useTableQuery<BalanceFilter>('all');
  const { data, isLoading, isFetching, error } = useBalancesQuery(pageQuery, isAuthenticated);
  const totalBalances = data?.total ?? 0;
  // The backend searches; the filter applies to the members of the current page.
  // ID sort: null = the order the API sends (also after a reload).
  const [idSort, setIdSort] = useState<SortDirection | null>(null);
  // Filter and ID sort apply to the rows of the current page.
  const balances = useMemo(() => {
    const rows = (data?.balances ?? []).filter((item) => matchesFilter(item, filter));
    if (!idSort) return rows;
    const direction = idSort === 'asc' ? 1 : -1;
    return rows.sort((a, b) => a.memberId.localeCompare(b.memberId, undefined, { numeric: true }) * direction);
  }, [data, filter, idSort]);
  const isNarrowed = filter !== 'all';
  const overview = data?.overview;

  const columns = useMemo(
    () => createColumns({ onOpenBalance: (item) => openDrawer('memberBalance', { id: item.memberId, name: item.memberName, memberNo: item.memberNo }) }),
    [openDrawer]
  );
  const show = (value: string) => (isLoading ? '--' : value);

  return (
    <div ref={pageRef} data-table-locked={tableLocked} className="group/page flex h-full flex-col overflow-y-auto p-5">
      <div className="mb-6 shrink-0">
        <div className="mb-4 flex items-center gap-2">
          <h1 className="text-lg font-bold text-gray-900">Balance Management</h1>
          {isFetching && !isLoading && (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#4369a0]/25 border-t-[#4369a0]" />
          )}
        </div>
        <p className="-mt-3 mb-4 text-sm text-gray-500">Member balances from Paid by PMS payments, interest and repayments</p>
        <div className="h-px bg-gray-200/80" />
      </div>

      <div className="mb-5 grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          icon={Coins}
          tone="brand"
          label="Balance Due"
          value={show(formatCurrency(overview?.balanceDue ?? 0))}
          rows={[
            ['Members With Balance', String(overview?.membersWithBalance ?? 0)],
            ['Incl. Interest', formatCurrency(overview?.interestDue ?? 0)],
          ]}
        />
        <SummaryCard
          icon={IndianRupee}
          tone="gray"
          label="Paid by PMS"
          value={show(formatCurrency(overview?.paidByPms ?? 0))}
          rows={[
            ['Policy', formatCurrency(overview?.paidByPmsPolicy ?? 0)],
            ['Loan', formatCurrency(overview?.paidByPmsLoan ?? 0)],
          ]}
        />
        <SummaryCard
          icon={AlertCircle}
          tone="amber"
          label="Interest Charged"
          value={show(formatCurrency(overview?.interestCharged ?? 0))}
          rows={[
            ['This Month', formatCurrency(overview?.interestThisMonth ?? 0)],
            ['Rate', `${BALANCE_RATE * 100}% / year`],
          ]}
        />
        <SummaryCard
          icon={CheckCircle2}
          tone="emerald"
          label="Repaid"
          value={show(formatCurrency(overview?.repaid ?? 0))}
          rows={[
            ['Cleared Members', String(overview?.clearedMembers ?? 0)],
            ['Repayments', String(overview?.repayments ?? 0)],
          ]}
        />
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Unable to load balances. Please try again.
        </div>
      )}

      <Card className="flex h-full shrink-0 flex-col border-gray-200/80 shadow-sm">
        <CardContent className="flex min-h-0 flex-1 flex-col p-5">
          <div className="mb-4 flex shrink-0 flex-wrap items-center gap-3">
            <h2 className="whitespace-nowrap text-sm font-semibold text-gray-900">
              Balances
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{isNarrowed ? balances.length : totalBalances}</span>
            </h2>

            <div className="relative min-w-[220px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by ID, card no, name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 border-gray-200 bg-white pl-9"
              />
            </div>

            <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1">
              {FILTERS.map((item) => (
                <Button
                  key={item.id}
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFilter(item.id)}
                  className={`h-8 px-3 text-xs ${
                    filter === item.id
                      ? 'bg-[#4369a0]/10 font-medium text-[#4369a0] hover:bg-[#4369a0]/15'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
            {isLoading ? (
              <div className="flex h-full flex-col items-center justify-center rounded-xl border border-gray-200/80 bg-white">
                <span className="h-8 w-8 animate-spin rounded-full border-2 border-[#4369a0]/30 border-t-[#4369a0]" />
                <p className="mt-3 text-sm text-gray-500">Loading balances...</p>
              </div>
            ) : (
              <DataTable
                items={balances}
                columns={columns}
                getKey={(item) => item.memberId}
                emptyIcon={Coins}
                emptyTitle="No balances found"
                itemLabel="members"
                currentPage={page}
                pageSize={pageSize}
                totalItems={totalBalances}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                firstColumnSort={{ direction: idSort, onToggle: () => setIdSort(nextSortDirection) }}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
